import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../context/Context';
import { fetchSubjectsByMockTest, getMockTestBySlug } from '../../services/mockTests.util';
import { checkMockTestPurchase } from '../../services/mockTestsPurchase.util';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClipboardList, faLock, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import MetaComponent from '../../components/common/MetaComponent';

const RENEWAL_MESSAGE = 'Your subscription has ended. Please renew it to continue accessing this content.';

const MockTestPage = () => {
  const { mockTestCode } = useParams();
  const navigate = useNavigate();
  const { loggedIn } = useAuthContext();

  const [mockTestData, setMockTestData] = useState(null);
  const [mockTestLoading, setMockTestLoading] = useState(true);
  const [mockTestError, setMockTestError] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [subjectsLoading, setSubjectsLoading] = useState(true);
  const [subjectsError, setSubjectsError] = useState(null);
  const [purchaseStatus, setPurchaseStatus] = useState(null);

  useEffect(() => {
    if (!loggedIn) {
      navigate('/login', { replace: true });
    }
  }, [loggedIn, navigate]);

  useEffect(() => {
    if (!loggedIn) return;
    const load = async () => {
      try {
        setMockTestLoading(true);
        setMockTestError(null);
        const data = await getMockTestBySlug(mockTestCode);
        setMockTestData(data);
      } catch (err) {
        setMockTestError(err.message || 'Failed to load mock test package.');
      } finally {
        setMockTestLoading(false);
      }
    };
    load();
  }, [mockTestCode, loggedIn]);

  useEffect(() => {
    if (!loggedIn) return;
    const load = async () => {
      try {
        setSubjectsLoading(true);
        setSubjectsError(null);
        const data = await fetchSubjectsByMockTest(mockTestCode);
        setSubjects(data || []);
      } catch (err) {
        setSubjectsError(err.message || 'Failed to load subjects.');
      } finally {
        setSubjectsLoading(false);
      }
    };
    load();
  }, [mockTestCode, loggedIn]);

  useEffect(() => {
    if (!loggedIn || !mockTestData) return;
    const check = async () => {
      try {
        const response = await checkMockTestPurchase(mockTestData._id);
        setPurchaseStatus(response.data);
      } catch (err) {
        setPurchaseStatus({ hasPurchased: false });
      }
    };
    check();
  }, [loggedIn, mockTestData]);

  const loading = mockTestLoading || subjectsLoading;
  if (loading) {
    return (
      <div className="main-content">
        <div className="container-fluid px-4 py-5">
          <div className="text-center text-white">
            <div className="spinner-border text-primary" role="status" />
            <p className="mt-3">Loading mock test package...</p>
          </div>
        </div>
      </div>
    );
  }

  if (mockTestError || subjectsError) {
    return (
      <div className="main-content">
        <div className="container-fluid px-4 py-5">
          <div className="text-center text-white">
            <FontAwesomeIcon icon={faClipboardList} size="3x" className="mb-3 text-secondary" />
            <h5>Error Loading Data</h5>
            <p className="text-light mb-4">{mockTestError || subjectsError}</p>
            <button className="btn btn-primary" onClick={() => window.location.reload()}>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!mockTestData) {
    return (
      <div className="main-content">
        <div className="container-fluid px-4 py-5">
          <div className="text-center text-white">
            <h5>Mock Test Package Not Found</h5>
            <p className="text-light">The requested mock test package could not be found.</p>
          </div>
        </div>
      </div>
    );
  }

  const hasAccess = purchaseStatus?.hasPurchased || mockTestData.isFree || !mockTestData.isPurchasable;
  const isSubscriptionExpired = purchaseStatus?.isExpired;
  const canExplore = (subject) => hasAccess;

  return (
    <div className="main-content">
      <MetaComponent
        title={`${mockTestData.title} || GVK Student Portal`}
        description={mockTestData.description || 'Mock tests and practice papers.'}
      />
      <div className="container-fluid px-4 py-4">
        <div className="row mb-4">
          <div className="col-12">
            <h2 className="text-white mb-2">{mockTestData.title}</h2>
            {mockTestData.description && (
              <p className="text-light mb-0">{mockTestData.description}</p>
            )}
            {!hasAccess && mockTestData.isPurchasable && mockTestData.price > 0 && (
              <p className="text-white mt-2 mb-0">
                Price: ₹{mockTestData.discountPrice ?? mockTestData.price}
                {mockTestData.discountPrice && (
                  <span className="text-muted text-decoration-line-through ms-2">
                    ₹{mockTestData.price}
                  </span>
                )}
              </p>
            )}
            {isSubscriptionExpired && (
              <p className="text-warning mt-2 mb-0">
                {purchaseStatus.message || RENEWAL_MESSAGE}
              </p>
            )}
          </div>
        </div>

        <div className="row mb-4">
          <div className="col-12">
            <h4 className="text-white mb-2">Subjects</h4>
            <p className="text-light mb-0">
              {subjects.length} subject{subjects.length !== 1 ? 's' : ''} • Mock tests available per subject
            </p>
          </div>
        </div>

        {subjects.length === 0 ? (
          <div className="text-center py-5 text-white">
            <FontAwesomeIcon icon={faClipboardList} size="3x" className="mb-3 opacity-50" />
            <h5>No Subjects Yet</h5>
            <p className="text-light">No subjects are linked to this mock test package yet.</p>
          </div>
        ) : (
          <div className="row g-4">
            {subjects.map((subject, index) => (
              <div key={subject._id || index} className="col-lg-4 col-md-6">
                <div className="card h-100 bg-dark border border-secondary border-opacity-25 rounded-3 overflow-hidden">
                  <div className="card-body d-flex flex-column">
                    <h5 className="text-white mb-2">{subject.title}</h5>
                    {subject.description && (
                      <p className="text-light small mb-3 flex-grow-1">{subject.description}</p>
                    )}
                    <div className="d-flex justify-content-between small text-light mb-3">
                      <span>Mock tests: {subject.counts?.mockTests ?? subject.counts?.mockTests ?? 0}</span>
                    </div>
                    <div className="mt-auto">
                      {canExplore(subject) ? (
                        <button
                          className="btn btn-light w-100"
                          onClick={() => navigate(`/mock-tests/${mockTestCode}/subject/${subject._id}`)}
                        >
                          <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                          Open Tests
                        </button>
                      ) : (
                        <button className="btn btn-secondary w-100" disabled>
                          <FontAwesomeIcon icon={faLock} className="me-2" />
                          {isSubscriptionExpired ? 'Renew Access' : 'Purchase to Access'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MockTestPage;
